import React from "react";
import moment from "moment";
import {
    formatDayHeader,
    getCheckoutGuests,
    getOccupancyStats,
    toDateKey
} from "pages/hoteles/utils/ocupacionMatrix";
import styles from "./Ocupacion.module.scss";
import { Circle } from "assets/icons";

const TODAY = moment().format("YYYY-MM-DD");
const X_ROWS_LIMIT = 9;

// ─── Utils extraíbles a ocupacionMatrix.js ───────────────────────────────────

export const getHoraActual = () => new Date().toTimeString().split(' ')[0];

export const getPendingCheckoutCount = (checkoutGuests, dateKey, horaActual) => {
    return checkoutGuests.filter((g) => {
        if (g.reservaStatusCode !== "I") return false;
        if (dateKey === TODAY) {
            const horaLimite = g.SFHotelCheckout || "09:00:00";
            return horaActual > horaLimite;
        }
        if (dateKey < TODAY) return true;
        return false;
    }).length;
};

export const getReservasCheckinCount = (guests, dateKey) =>
    getReservasActivasCount(guests, dateKey);

export const getReservasActivasCount = (guests, dateKey) =>
    guests.filter(g => {
        if (g.reservaStatus !== 'Reservada') return false;
        const desde = toDateKey(g.fechaDesde);
        const hasta = toDateKey(g.fechaHasta);
        const vencida = hasta < TODAY;

        if (vencida) return dateKey === desde;

        return dateKey >= desde && dateKey < hasta;
    }).length;

export const calcularPlusYOcupados = (checkoutGuests, ocupados, plazas, dateKey, horaActual) => {
    const pendingCount = getPendingCheckoutCount(checkoutGuests, dateKey, horaActual);
    let plus = pendingCount;
    if (plazas != null && ocupados + plus > Number(plazas)) {
        plus = Math.max(0, Number(plazas) - ocupados);
    }
    return { plus, hasPendingCheckout: pendingCount > 0 };
};

// ─────────────────────────────────────────────────────────────────────────────

function CellOccupancySummary({ guests, plazas, dateKey }) {
    const horaActual = getHoraActual();
    const { ocupados, plazas: totalPlazas } = getOccupancyStats(guests, plazas, dateKey);
    const checkoutGuests = getCheckoutGuests(guests, dateKey);

    // Solo para el ícono de alerta, ya no suma al conteo
    const { hasPendingCheckout } = calcularPlusYOcupados(checkoutGuests, ocupados, plazas, dateKey, horaActual);

    const ocupadosFinal = ocupados; // ← sin plus, ya está incluido en occupiesPlazaOnDate
    const libresBase = totalPlazas != null ? Math.max(0, totalPlazas - ocupadosFinal) : null;

    const reservasCheckinCount = getReservasCheckinCount(guests, dateKey);
    // const reservasActivasCount = getReservasActivasCount(guests, dateKey);
    const reservasCount = getReservasActivasCount(guests, dateKey);
    const libresFinal = libresBase != null ? libresBase - reservasCount : null;

    if (totalPlazas == null) {
        return (
            <div className={styles.cellOccupancy}>
                <div className={styles.metricsContainer}>
                    <span className={styles.metricItem}>{ocupadosFinal}</span>
                    {reservasCheckinCount > 0 && (
                        <span className={styles.metricItem}>{reservasCount}</span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.cellWrapper}>
            {hasPendingCheckout && (
                <img
                    src={Circle}
                    alt="warning"
                    width="7"
                    height="7"
                    className={styles.cellWarningIcon}
                />
            )}
            <div className={styles.metricsContainer}>
                <span
                    className={`${styles.cellOccupancyReserved} ${styles.metricBlock}`}
                    style={{ color: reservasCheckinCount === 0 ? '#1d57b372' : '#1d56b3' }}
                >
                    <strong>{reservasCheckinCount}</strong>
                </span>
                <span
                    className={`${styles.metricBlock} ${styles.cellOccupancyOcup}`}
                    style={{ color: ocupadosFinal === 0 ? '#ef262674' : '#ef2626' }}
                >
                    <strong>{ocupadosFinal}</strong>
                </span>
                <span className={`${libresFinal === 0 ? styles.cellOccupancyFull : styles.cellOccupancyFree} ${styles.metricBlock}`}>
                    <strong>{libresFinal}</strong>
                </span>
            </div>
        </div>
    );
}

export default function OcupacionMatrix({
    habitaciones,
    days,
    matrix,
    loading,
    onCellClick,
}) {
    if (loading) {
        return (
            <div className={styles.loadingOverlay}>
                <span>Cargando matriz de ocupación…</span>
            </div>
        );
    }

    if (!habitaciones.length) {
        return (
            <p className={styles.noRooms}>
                No hay habitaciones para el hotel seleccionado.
            </p>
        );
    }

    const horaActual = getHoraActual();
    const isLongTable = habitaciones.length > X_ROWS_LIMIT;

    const handleCellClick = (hab, day, guests) => {
        if (!guests.length) return;
        onCellClick({
            habitacion: hab,
            dateKey: day.format("YYYY-MM-DD"),
            fechaLabel: day.format("DD/MM/YYYY"),
            guests,
        });
    };

    const mostrarLeyendaGlobal = habitaciones.some((hab) => {
        const roomData = matrix[String(hab.id)] ?? {};
        return days.some((day) => {
            const dateKey = day.format("YYYY-MM-DD");
            const checkoutGuests = getCheckoutGuests(roomData[dateKey] ?? [], dateKey);
            return getPendingCheckoutCount(checkoutGuests, dateKey, horaActual) > 0;
        });
    });

    const calcularTotalesPorDia = (day) => {
        const dateKey = day.format("YYYY-MM-DD");
        let totalOcupados = 0;
        let totalLibres = 0;
        let totalReservados = 0;

        habitaciones.forEach((hab) => {
            const guests = (matrix[String(hab.id)] ?? {})[dateKey] ?? [];
            const { ocupados, plazas } = getOccupancyStats(guests, hab.plazas, dateKey);
            const libresBase = plazas != null ? Math.max(0, plazas - ocupados) : 0;

            totalOcupados += ocupados;
            totalLibres += libresBase - getReservasActivasCount(guests, dateKey);
            totalReservados += getReservasCheckinCount(guests, dateKey);
        });

        return { totalOcupados, totalLibres, totalReservados };
    };

    const verificarCeldaInactiva = (hab, dateKey) => {
        const historicoStr = hab.historico;

        if (historicoStr) {
            const registros = historicoStr
                .split(";")
                .map(item => item.trim())
                .filter(Boolean)
                .map(item => {
                    const lastColonIdx = item.lastIndexOf(":");
                    return {
                        datetimeStr: item.substring(0, lastColonIdx).trim(),
                        estado: item.substring(lastColonIdx + 1).trim(),
                    };
                })
                .sort((a, b) => a.datetimeStr.localeCompare(b.datetimeStr));

            const ultimoCambioPrevio = [...registros]
                .reverse()
                .find(reg => reg.datetimeStr <= `${dateKey} 23:59:59`);

            if (ultimoCambioPrevio) return ultimoCambioPrevio.estado === 'N';
        }

        if (hab.estado !== 'N') return false;

        const fechaRaw = hab.ultimoEstado;
        if (!fechaRaw) return true;

        let fechaISO = "";

        if (typeof fechaRaw === 'string' && fechaRaw.includes('/')) {
            const parts = fechaRaw.trim().split('/');
            if (parts.length === 3) {
                fechaISO = `${parts[2].substring(0, 4)}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            }
        }

        if (!fechaISO) {
            fechaISO = String(fechaRaw).trim().substring(0, 10);
            if (!/^\d{4}-\d{2}-\d{2}$/.test(fechaISO)) {
                fechaISO = moment(fechaRaw).isValid() ? moment(fechaRaw).format("YYYY-MM-DD") : "";
            }
        }

        return fechaISO ? dateKey >= fechaISO : true;
    };

    const totalPlazasHotel = habitaciones.reduce((acc, hab) => acc + (hab.plazas ? Number(hab.plazas) : 0), 0);

    return (
        <div className={`${styles.wrapper} ${isLongTable ? styles.wrapperLong : ""}`}>
            {mostrarLeyendaGlobal && (
                <div className={styles.legend}>
                    <img src={Circle} alt="warning" />
                    <span>Check-out pendiente</span>
                </div>
            )}
            <div className={styles.matrixScroll}>
                <table className={styles.matrix}>
                    <thead>
                        <tr>
                            <th className={`${styles.roomHeader} ${styles.roomHeaderMain}`}>
                                <span className={styles.roomHeaderLabel}>Habitación</span>
                            </th>
                            {days.map((day) => {
                                const dateKey = day.format("YYYY-MM-DD");
                                const isToday = dateKey === TODAY;
                                return (
                                    <th
                                        key={dateKey}
                                        className={isToday ? styles.todayHeaderColumn : styles.dayHeaderColumn}
                                    >
                                        <div className={styles.dayTitleText}>
                                            {formatDayHeader(day)}
                                        </div>
                                        <div className={styles.subHeaderCategories}>
                                            <small className={styles.catRes}>Res</small>
                                            <small className={styles.catOcu}>Ocu</small>
                                            <small className={styles.catLibre}>Libre</small>
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {habitaciones.map((hab) => {
                            const isDisabled = hab.estado === 'N';
                            const roomData = matrix[String(hab.id)] ?? {};
                            return (
                                <tr key={hab.id}>
                                    <td
                                        className={styles.roomCell}
                                        style={{ color: isDisabled ? '#bdbdbdff' : '#000000ff' }}
                                    >
                                        {isDisabled && (
                                            <div style={{ position: "absolute", top: '2px', right: '10px' }}>
                                                <small style={{ color: '#ff7402a0', background: '#FF74021A', padding: '1px 4px 2px', borderRadius: '10px', fontSize: '0.55rem', fontWeight: '600' }}>
                                                    No disponible
                                                </small>
                                            </div>
                                        )}
                                        <span className={styles.roomNumero}>{hab.numero}</span>
                                        <div className={styles.roomMetaWrapper}>
                                            <span className={styles.roomMeta}>
                                                {hab.genero ?? null}
                                            </span>
                                            <span>-</span>
                                            {hab.plazas != null && hab.plazas !== "" && (
                                                <span
                                                    className={styles.roomPlazas}
                                                    style={{ color: isDisabled ? '#a8a8a8ff' : '' }}
                                                >
                                                    {hab.plazas} Plazas
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    {days.map((day) => {
                                        const dateKey = day.format("YYYY-MM-DD");
                                        const isToday = dateKey === TODAY;
                                        const guests = roomData[dateKey] ?? [];
                                        const hasGuests = guests.length > 0;
                                        const isCellDisabled = verificarCeldaInactiva(hab, dateKey);
                                        return (
                                            <td
                                                key={dateKey}
                                                className={[
                                                    styles.cell,
                                                    isToday ? styles.todayColumn : "",
                                                    hasGuests ? styles.cellClickable : "",
                                                    isCellDisabled ? styles.isDisabled : "",
                                                ].filter(Boolean).join(" ")}
                                                onClick={() => handleCellClick(hab, day, guests)}
                                                onKeyDown={(e) => {
                                                    if (hasGuests && (e.key === "Enter" || e.key === " ")) {
                                                        e.preventDefault();
                                                        handleCellClick(hab, day, guests);
                                                    }
                                                }}
                                                role={hasGuests ? "button" : undefined}
                                                tabIndex={hasGuests ? 0 : undefined}
                                                aria-label={hasGuests ? `Ver ${guests.length} reserva(s) - habitación ${hab.numero}, ${day.format("DD/MM/YYYY")}` : undefined}
                                            >
                                                <CellOccupancySummary
                                                    guests={guests}
                                                    plazas={hab.plazas}
                                                    dateKey={dateKey}
                                                />
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                        <tr className={styles.spacerRow}>
                            <td colSpan={days.length + 1}></td>
                        </tr>
                    </tbody>
                    <tfoot className={styles.matrixTotalFoot}>
                        <tr>
                            <td className={`${styles.roomCell} ${styles.totalRowHeader}`}>
                                <strong>TOTALES</strong>
                                {totalPlazasHotel > 0 && (
                                    <div className={styles.totalPlazasSubtitle}>
                                        {totalPlazasHotel} Plazas
                                    </div>
                                )}
                            </td>
                            {days.map((day) => {
                                const dateKey = day.format("YYYY-MM-DD");
                                const isToday = dateKey === TODAY;
                                const { totalOcupados, totalLibres, totalReservados } = calcularTotalesPorDia(day);
                                return (
                                    <td
                                        key={`total-${dateKey}`}
                                        className={[
                                            styles.totalCell,
                                            isToday ? styles.todayColumn : "",
                                        ].filter(Boolean).join(" ")}
                                    >
                                        <div className={styles.metricsContainer}>
                                            <span className={`${styles.metricBlock} ${styles.cellOccupancyReserved}`}>
                                                <strong>{totalReservados}</strong>
                                            </span>
                                            <span className={`${styles.cellOccupancyOcup} ${styles.metricBlock}`}>
                                                <strong>{totalOcupados}</strong>
                                            </span>
                                            <span className={`${styles.metricBlock} ${totalLibres === 0 ? styles.cellOccupancyFull : styles.cellOccupancyFree}`}>
                                                <strong>{totalLibres}</strong>
                                            </span>
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}